import { Injectable, inject } from '@angular/core'
import { LogService } from './log.service'
import { FinanceType, IFinanceLinkData } from '../interfaces/config.interface'
import { IAsset } from '../interfaces/asset.interface'
import { ModalService } from './modal.service'
import { GlobalDataService } from './global-data.service'
import { ToastService } from './toast.service'

@Injectable({
  providedIn: 'root',
})
export class FinanceService {
  private _logger = inject(LogService)
  private _globalDataService = inject(GlobalDataService)
  private _modalService = inject(ModalService)
  private _toastService = inject(ToastService)

  get type(): FinanceType | undefined {
    return this._globalDataService.config().financing?.type
  }

  set type(value: FinanceType | undefined) {
    this._globalDataService.config().financing = {
      ...this._globalDataService.config().financing,
      type: value,
    }
  }

  get data(): IFinanceLinkData | undefined {
    switch (this.type) {
      case 'link':
        return this._globalDataService.config().financing.data
      default:
        this._logger.error(`Unsupported financing type: ${this.type}`)
        return undefined
    }
  }

  set data(value: IFinanceLinkData | undefined) {
    this._globalDataService.config().financing = {
      ...this._globalDataService.config().financing,
      data: value,
    }
  }

  validateSettings(): boolean {
    if (this.type === undefined) {
      return false
    }
    if (this.type === 'link') {
      const linkData = this.data as IFinanceLinkData
      if (!linkData || !linkData.url || !linkData.target) {
        return false
      }
    }
    return true
  }

  buildUrl(asset?: IAsset): string {
    if (!asset) {
      return this.data?.url ?? ''
    }

    const { equipment_number, year, make, model, list_price } = asset
    const baseUrl = this.data?.url

    if (!baseUrl) {
      this._logger.error('No base URL found for financing')
      return ''
    }

    const urlParams = this.data?.url_parameters ?? ''
    if (!urlParams) {
      return baseUrl
    }

    const params = urlParams
      .replace('<assetId>', equipment_number)
      .replace('<year>', String(year))
      .replace('<make>', make)
      .replace('<model>', model)
      .replace('<price>', String(list_price))
    return baseUrl + params
  }

  openFinanceLink(asset?: IAsset): void {
    if (!this.validateSettings()) {
      this._toastService.error({
        message: $localize`:@@finance.error:Unable to open finance link.`,
      })
      this._logger.error('Unable to open finance link.  Financing settings are not valid')
      return
    }
    if (this.type === 'link') {
      const {
        target,
      } = this.data as IFinanceLinkData
      if (target === 'iframe') {
        this._modalService.iframe({
          src: this.buildUrl(asset),
        })
      } else {
        window.open(
          this.buildUrl(asset),
          target === 'new_tab' ? '_blank' : '_self',
          'noopener,noreferrer',
        )
      }
    }
  }
}
